import * as React from 'react';
import { toast as sonnerToast } from 'sonner';

export type ToastVariant = 'default' | 'destructive' | 'success';

export interface ToastProps {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactElement;
  variant?: ToastVariant;
  duration?: number;
}

type ToastController = {
  (props: ToastProps): string | number;
  success: typeof sonnerToast.success;
  error: typeof sonnerToast.error;
  info: typeof sonnerToast.info;
  warning: typeof sonnerToast.warning;
  promise: typeof sonnerToast.promise;
  dismiss: typeof sonnerToast.dismiss;
};

const mapVariantToMethod = (variant?: ToastVariant) => {
  switch (variant) {
    case 'destructive':
      return sonnerToast.error;
    case 'success':
      return sonnerToast.success;
    default:
      return sonnerToast;
  }
};

const showToast: ToastController = ((props: ToastProps) => {
  const { title, description, variant, action, duration, id } = props;
  const message = title ?? description ?? '';

  const toastFn = mapVariantToMethod(variant);

  return toastFn(message, {
    id,
    description: title && description ? description : undefined,
    action,
    duration,
  });
}) as ToastController;

showToast.success = sonnerToast.success;
showToast.error = sonnerToast.error;
showToast.info = sonnerToast.info;
showToast.warning = sonnerToast.warning;
showToast.promise = sonnerToast.promise;
showToast.dismiss = sonnerToast.dismiss;

export function useToast() {
  return React.useMemo(
    () => ({
      toast: showToast,
      dismiss: sonnerToast.dismiss,
      toasts: [] as Array<{ id: string | number }>,
    }),
    []
  );
}

export { showToast as toast };
